import { apiService } from '@/services/api'
import type { LoginCredentials, PasswordChangeData, ProfileUpdateData, User } from '@/types/api'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

console.log('🔍 [USER STORE] apiService:', apiService)
console.log('🔍 [USER STORE] apiService.client:', apiService.client)
console.log('🔍 [USER STORE] setAuthToken method:', apiService.client?.setAuthToken)

// Helper function pour configurer le token d'authentification
const setAuthToken = (token: string | null) => {
  try {
    // Vérifier que apiService.client existe et a la méthode setAuthToken
    if (apiService && apiService.client && typeof apiService.client.setAuthToken === 'function') {
      apiService.client.setAuthToken(token)
      console.log('✅ [USER STORE] Token configuré:', token ? 'présent' : 'supprimé')
    } else {
      console.error('❌ [USER STORE] setAuthToken non disponible')
      console.log('Debug - apiService:', apiService)
      console.log('Debug - apiService.client:', apiService?.client)
      console.log('Debug - setAuthToken method:', apiService?.client?.setAuthToken)

      // Fallback: stocker dans localStorage pour usage ultérieur
      if (token) {
        localStorage.setItem('auth_token', token)
      } else {
        localStorage.removeItem('auth_token')
      }
    }
  } catch (error) {
    console.error('❌ [USER STORE] Erreur lors de la configuration du token:', error)
  }
}

export const useUserStore = defineStore('user', () => {
  // Ajout du type d'utilisateur courant
  const userType = ref<'candidate' | 'hr' | 'admin' | null>(null)

  // Stockage séparé pour chaque espace
  const getStorageKey = (key: string) => userType.value ? `${key}_${userType.value}` : key

  const user = ref<User | null>(null)
  const token = ref<string | null>(null)
  const isLoading = ref<boolean>(false)
  const isInitialized = ref<boolean>(false)

  // Initialisation selon l'espace
  const setUserType = (type: 'candidate' | 'hr' | 'admin' | null) => {
    userType.value = type
    // Charger le token et l'utilisateur pour cet espace
    token.value = localStorage.getItem(getStorageKey('auth_token'))
    const userRaw = localStorage.getItem(getStorageKey('user'))
    user.value = userRaw ? JSON.parse(userRaw) : null
    // Configurer le token pour l'API
    setAuthToken(token.value)
    isInitialized.value = true
  }

  // Réinitialiser le store (utile lors du changement d'espace)
  const resetUserStore = () => {
    user.value = null
    token.value = null
    userType.value = null
    isInitialized.value = false
  }

  const isAuthenticated = computed(() => !!token.value && !!user.value)

  const hasRole = (role: string): boolean => {
    if (!user.value || !user.value.roles) return false
    return user.value.roles.includes(role)
  }

  const login = async (credentials: LoginCredentials): Promise<{ success: boolean; user: User }> => {
    isLoading.value = true
    try {
      const response = await apiService.auth.login(credentials)
      if (response.data.success) {
        user.value = response.data.user
        token.value = response.data.token
        // Stockage séparé par espace
        localStorage.setItem(getStorageKey('auth_token'), response.data.token)
        localStorage.setItem(getStorageKey('user'), JSON.stringify(response.data.user))
        setAuthToken(response.data.token)
        return { success: true, user: response.data.user }
      } else {
        throw new Error(response.data.message || 'Erreur de connexion')
      }
    } catch (error: any) {
      console.error('Erreur de connexion:', error)
      throw new Error(error.response?.data?.message || 'Erreur de connexion')
    } finally {
      isLoading.value = false
    }
  }

  const logout = async (): Promise<void> => {
    try {
      await apiService.auth.logout()
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error)
    } finally {
      user.value = null
      token.value = null
      // Nettoyer le stockage pour l'espace courant
      localStorage.removeItem(getStorageKey('auth_token'))
      localStorage.removeItem(getStorageKey('user'))
      setAuthToken(null)
    }
  }

  const fetchProfile = async () => {
    try {
      const response = await apiService.auth.getProfile()
      if (response.data.success) {
        user.value = response.data.user
        localStorage.setItem(getStorageKey('user'), JSON.stringify(response.data.user))
      }
      return response.data
    } catch (error) {
      console.error('Erreur lors de la récupération du profil:', error)
      logout()
      throw error
    } finally {
      isInitialized.value = true
    }
  }

  const updateProfile = async (profileData: ProfileUpdateData) => {
    isLoading.value = true
    try {
      const response = await apiService.auth.updateProfile(profileData)
      if (response.data.success) {
        user.value = { ...user.value!, ...response.data.user }
      }
      return response.data
    } catch (error) {
      console.error('Erreur lors de la mise à jour du profil:', error)
      throw error
    } finally {
      isLoading.value = false
    }
  }

  const changePassword = async (passwordData: PasswordChangeData) => {
    isLoading.value = true
    try {
      const response = await apiService.auth.changePassword(passwordData)
      return response.data
    } catch (error) {
      console.error('Erreur lors du changement de mot de passe:', error)
      throw error
    } finally {
      isLoading.value = false
    }
  }

  // Initialiser le token si présent au démarrage
  if (!userType.value) {
    // Par défaut, pas d'espace sélectionné
    isInitialized.value = true
  } else if (token.value) {
    setAuthToken(token.value)
    fetchProfile().catch(() => { })
  }

  return {
    user,
    token,
    userType,
    isLoading,
    isInitialized,
    isAuthenticated,
    hasRole,
    setUserType,
    resetUserStore,
    login,
    logout,
    fetchProfile,
    updateProfile,
    changePassword
  }
})
